import type { WidgetConfig } from '../types';
import { h } from '../utils/dom';
import { getChatIcon, getCloseIcon, getMarkSvg } from './mark';

export class Trigger {
  private root: ShadowRoot;
  private el: HTMLButtonElement;
  private badge: HTMLSpanElement;
  private proactive: HTMLDivElement | null = null;
  private isOpen = false;
  private onClick: () => void;

  constructor(root: ShadowRoot, config: WidgetConfig, onClick: () => void) {
    this.root = root;
    this.onClick = onClick;

    const cfg = config.config;
    const triggerType = cfg.behaviorTrigger ?? 'logo';
    const isCircle = triggerType === 'circle';
    const isLogo = triggerType === 'logo';
    const position = cfg.behaviorPosition;

    // Button wrapper
    this.el = h('button', {
      class: `att-trigger att-trigger--${isLogo ? 'logo' : isCircle ? 'circle' : 'pill'}`,
      'aria-label': cfg.behaviorTriggerLabel || 'Fale com a gente',
      type: 'button',
    }) as HTMLButtonElement;

    // Icon
    const iconWrap = h('span', { class: 'att-trigger-icon' });
    iconWrap.innerHTML = isLogo ? getMarkSvg('white', 60) : getChatIcon('white', 22);

    if (!isCircle && !isLogo) {
      const label = h('span', { class: 'att-trigger-label' }, cfg.behaviorTriggerLabel);
      this.el.appendChild(iconWrap);
      this.el.appendChild(label);
    } else {
      this.el.appendChild(iconWrap);
    }

    // Badge
    this.badge = h('span', { class: 'att-badge att-badge--hidden' }) as HTMLSpanElement;
    this.el.appendChild(this.badge);

    // Position
    const posStyle = position === 'left'
      ? 'position:fixed;bottom:24px;left:24px;'
      : 'position:fixed;bottom:24px;right:24px;';
    this.el.style.cssText = posStyle;

    this.el.addEventListener('click', () => {
      this.onClick();
    });

    root.appendChild(this.el);
  }

  setOpen(open: boolean): void {
    this.isOpen = open;
    const iconWrap = this.el.querySelector('.att-trigger-icon');
    const isLogo = this.el.classList.contains('att-trigger--logo');
    if (iconWrap) {
      if (open) {
        iconWrap.innerHTML = getCloseIcon(isLogo ? '#0055FF' : 'white', 22);
      } else {
        iconWrap.innerHTML = isLogo ? getMarkSvg('white', 60) : getChatIcon('white', 22);
      }
    }
    if (open && this.proactive) {
      this.hideProactive();
    }
  }

  setUnread(count: number): void {
    if (this.isOpen || count === 0) {
      this.badge.className = 'att-badge att-badge--hidden';
      return;
    }
    this.badge.className = 'att-badge';
    this.badge.textContent = count > 9 ? '9+' : String(count);
  }

  showProactive(message: string): void {
    if (this.proactive) return;

    this.proactive = h('div', { class: 'att-proactive' }) as HTMLDivElement;
    this.proactive.textContent = message;

    const closeBtn = h('button', { class: 'att-proactive-close', type: 'button', 'aria-label': 'Fechar' }, '×');
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.hideProactive();
    });
    this.proactive.appendChild(closeBtn);

    this.proactive.addEventListener('click', () => {
      this.hideProactive();
      this.onClick();
    });

    this.root.appendChild(this.proactive);
  }

  hideProactive(): void {
    if (this.proactive) {
      this.proactive.remove();
      this.proactive = null;
    }
  }

  remove(): void {
    this.hideProactive();
    this.el.remove();
  }
}
